/**
 * CommittedDateInput — natives Datumsfeld, das erst bei bewusster Übernahme meldet (Issue #85).
 *
 * Der native Kalender von Edge/Chromium feuert beim Blättern zwischen Monaten bereits
 * `change` mit dem verschobenen Datum. Wer daran direkt speichert, schreibt Werte in die
 * DB, die niemand ausgewählt hat. Deshalb hält das Feld einen lokalen Entwurf und ruft
 * `onCommit` erst beim Verlassen (blur) oder mit Enter — und nur, wenn sich der Wert
 * vom Server-Wert unterscheidet.
 *
 * Natives Feld bewusst beibehalten (Weg A aus #85): auf Mobilgeräten ist der native
 * Picker der bessere Weg (#84).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export function CommittedDateInput({
  value,
  onCommit,
  canEdit,
  busy,
}: {
  /** Gespeicherter Server-Wert (`YYYY-MM-DD`) oder leer. */
  value: string | null | undefined;
  onCommit: (next: string | null) => void;
  canEdit: boolean;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const saved = value ?? '';
  const [draft, setDraft] = useState(saved);
  const inputRef = useRef<HTMLInputElement>(null);
  // Zuletzt abgeschickter Wert: verhindert, dass Enter + nachfolgendes blur denselben
  // Wert zweimal schreiben, solange der Server-Wert noch nicht zurück ist.
  const lastSent = useRef<string | null>(null);

  // Neuer Server-Wert (gespeichert oder von anderswo geändert) übernimmt den Entwurf —
  // aber nicht, während jemand im Feld arbeitet: ein Refetch darf keine laufende
  // Eingabe überschreiben.
  useEffect(() => {
    lastSent.current = null;
    if (document.activeElement !== inputRef.current) setDraft(saved);
  }, [saved]);

  const dirty = draft !== saved;

  const commit = () => {
    if (!dirty || draft === lastSent.current) return;
    lastSent.current = draft;
    onCommit(draft === '' ? null : draft);
  };

  return (
    <input
      ref={inputRef}
      type="date"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
      }}
      disabled={!canEdit || busy}
      title={
        !canEdit
          ? t('v2.reader.tooltip', 'Sie haben Leserechte')
          : dirty
            ? t('v2.dateinput.unsaved_hint', 'Noch nicht gespeichert — Enter oder Feld verlassen')
            : undefined
      }
      className="text-xs px-2 py-1 rounded outline-none"
      style={{
        background: 'var(--surface-2)',
        color: 'var(--ink)',
        border: `1px solid ${dirty ? 'var(--primary)' : 'var(--line-soft)'}`,
        fontFamily: 'var(--f-mono)',
        cursor: canEdit ? 'pointer' : 'not-allowed',
        opacity: canEdit && !busy ? 1 : 0.6,
      }}
    />
  );
}
